import React, { useState, useEffect } from 'react';
import apiClient from './services/api';

interface IntegrationSettings {
  id?: number;
  provider: 'local' | 'asistpro' | 'cloudbeds' | 'opera';
  is_active?: boolean;
  api_url?: string;
  api_email?: string;
  property_id?: string;
  connection_status?: string;
  last_sync_at?: string;
}

const HotelIntegrationTab: React.FC = () => {
  const [settings, setSettings] = useState<IntegrationSettings[]>([]);
  const [activeProvider, setActiveProvider] = useState<IntegrationSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Form state
  const [formData, setFormData] = useState<IntegrationSettings>({
    provider: 'local',
    api_url: '',
    api_email: '',
  });
  const [apiPassword, setApiPassword] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/hotel-integration/active');
      console.log('📥 Fetched settings:', response.data);
      
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setActiveProvider(data);
        
        // Her zaman formData'yı güncelle
        const newFormData = {
          provider: data.provider || 'local',
          api_url: data.api_url || '',
          api_email: data.api_email || '',
          property_id: data.property_id || '',
        };
        
        console.log('📝 Setting formData:', newFormData);
        setFormData(newFormData);
        
        // Password güvenlik için asla gelmez, boş bırak
        setApiPassword('');
      }
    } catch (error) {
      console.error('Ayarlar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const provider = e.target.value as IntegrationSettings['provider'];
    
    console.log('🔄 Provider changed to:', provider);
    setTestResult(null);

    if (provider === 'local') {
      // Local seçilince her şeyi temizle
      setFormData({ 
        provider: 'local',
        api_url: '',
        api_email: '',
        property_id: ''
      });
      setApiPassword('');
    } else {
      // Diğer providerlar için mevcut formData'yı koru, sadece provider'ı değiştir
      setFormData({ ...formData, provider });
      
      // Eğer alanlar boşsa ve AsistPro seçildiyse örnek değerler ver
      if (provider === 'asistpro' && !formData.api_url && !formData.api_email) {
        setFormData({
          provider: 'asistpro',
          api_url: 'https://panel.asistpro.net.tr',
          api_email: 'villapolikne@gmail.com',
          property_id: '',
        });
        setApiPassword('123456');
      }
    }
  };

  const handleTestConnection = async () => {
    try {
      setLoading(true);
      setTestResult(null);

      const response = await apiClient.post('/hotel-integration/test', {
        provider: formData.provider,
        api_url: formData.api_url,
        api_email: formData.api_email,
        api_password: apiPassword,
        property_id: formData.property_id,
      });

      setTestResult({
        success: response.data.success,
        message: response.data.message,
      });

      // Property ID otomatik bulunduysa form alanına yerleştir
      if (response.data.success && response.data.data?.propertyId) {
        setFormData({
          ...formData,
          property_id: response.data.data.propertyId,
        });
        setTestResult({
          success: true,
          message: `${response.data.message} - Property ID otomatik bulundu: ${response.data.data.propertyName || response.data.data.propertyId}`,
        });
      }
    } catch (error: any) {
      console.error('Test hatası:', error);
      setTestResult({
        success: false,
        message: error.response?.data?.message || error.message || 'Bağlantı test edilemedi',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const response = await apiClient.post('/hotel-integration', {
        provider: formData.provider,
        api_url: formData.api_url || null,
        api_email: formData.api_email || null,
        api_password: apiPassword || null,
        property_id: formData.property_id || null,
      });

      if (response.data.success) {
        alert('✅ Entegrasyon ayarları kaydedildi!');
        // Backend'in persist etmesi için kısa bir delay
        setTimeout(() => {
          fetchSettings();
        }, 500);
        setTestResult(null);
      }
    } catch (error: any) {
      console.error('Kayıt hatası:', error);
      alert('❌ Kayıt başarısız: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tab-content">
      <h2>🔗 Otel Yönetim Sistemi Entegrasyonu</h2>

      <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#e3f2fd', borderRadius: '8px', border: '1px solid #90caf9' }}>
        <p style={{ margin: 0, fontSize: '0.9rem', color: '#1976d2' }}>
          💡 <strong>Bilgi:</strong> Buradan farklı otel yönetim sistemleriyle entegrasyon yapabilirsiniz. 
          Oda rezervasyonları seçtiğiniz sisteme otomatik gönderilecektir. Şezlong, kahvaltı ve akşam yemeği rezervasyonları yerel sistemde kalır.
        </p>
      </div>

      {/* Aktif Provider Durumu */}
      {activeProvider && (
        <div style={{ marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>📊 Mevcut Durum</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <strong>Provider:</strong> {activeProvider.provider === 'local' ? '🏠 Yerel Sistem' : '🌐 ' + activeProvider.provider?.toUpperCase()}
            </div>
            <div>
              <strong>Durum:</strong> {' '}
              {activeProvider.connection_status === 'connected' ? (
                <span style={{ color: '#28a745' }}>✅ Bağlı</span>
              ) : activeProvider.connection_status === 'failed' ? (
                <span style={{ color: '#dc3545' }}>❌ Hata</span>
              ) : (
                <span style={{ color: '#6c757d' }}>⚪ Test Edilmedi</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Entegrasyon Formu */}
      <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '8px', border: '1px solid #dee2e6' }}>
        <h3 style={{ margin: '0 0 1.5rem 0' }}>⚙️ Entegrasyon Ayarları</h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Provider Seçimi */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Otel Yönetim Sistemi
            </label>
            <select
              value={formData.provider}
              onChange={handleProviderChange}
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '1rem',
                border: '1px solid #ced4da',
                borderRadius: '6px',
              }}
            >
              <option value="local">🏠 Yerel Sistem (Varsayılan)</option>
              <option value="asistpro">🌐 AsistPro (panel.asistpro.net.tr)</option>
              <option value="cloudbeds">☁️ CloudBeds (Yakında)</option>
              <option value="opera">🏢 Opera PMS (Yakında)</option>
            </select>
            <small style={{ color: '#6c757d', fontSize: '0.85rem' }}>
              {formData.provider === 'local' && 'Yerel veritabanı kullanılır. Oda rezervasyonu desteklenmez.'}
              {formData.provider === 'asistpro' && 'Reseller-panel sistemi ile tam entegrasyon.'}
              {(formData.provider === 'cloudbeds' || formData.provider === 'opera') && 'Bu entegrasyon henüz aktif değil.'}
            </small>
          </div>

          {/* AsistPro İçin Ayarlar */}
          {formData.provider === 'asistpro' && (
            <>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  API URL
                </label>
                <input
                  type="text"
                  value={formData.api_url}
                  onChange={(e) => setFormData({ ...formData, api_url: e.target.value })}
                  placeholder="https://panel.asistpro.net.tr"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={formData.api_email}
                  onChange={(e) => setFormData({ ...formData, api_email: e.target.value })}
                  placeholder="villapolikne@gmail.com"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Şifre
                </label>
                <input
                  type="password"
                  value={apiPassword}
                  onChange={(e) => setApiPassword(e.target.value)}
                  placeholder={activeProvider?.provider === 'asistpro' && formData.api_email ? "••••••• (Kayıtlı şifre kullanılacak)" : "123456"}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                  }}
                />
                {activeProvider?.provider === 'asistpro' && !apiPassword && formData.api_email && (
                  <small style={{ color: '#6c757d', fontSize: '0.85rem', display: 'block', marginTop: '0.25rem' }}>
                    💡 Boş bırakırsanız kayıtlı şifre kullanılır. Değiştirmek için yeni şifre girin.
                  </small>
                )}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Property ID <small style={{ color: '#6c757d', fontWeight: 'normal' }}>(Opsiyonel - Otomatik bulunur)</small>
                </label>
                <input
                  type="text"
                  value={formData.property_id || ''}
                  onChange={(e) => setFormData({ ...formData, property_id: e.target.value })}
                  placeholder="Boş bırakılırsa otomatik bulunur"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                  }}
                />
              </div>
            </>
          )}

          {/* Test Sonucu */}
          {testResult && (
            <div style={{
              padding: '1rem',
              backgroundColor: testResult.success ? '#d4edda' : '#f8d7da',
              border: `1px solid ${testResult.success ? '#c3e6cb' : '#f5c6cb'}`,
              borderRadius: '6px',
              color: testResult.success ? '#155724' : '#721c24',
            }}>
              <strong>{testResult.success ? '✅ Başarılı:' : '❌ Hata:'}</strong> {testResult.message}
            </div>
          )}

          {/* Butonlar */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <button
              onClick={handleTestConnection}
              disabled={loading || formData.provider === 'local'}
              style={{
                flex: 1,
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                fontWeight: '600',
                color: 'white',
                backgroundColor: '#007bff',
                border: 'none',
                borderRadius: '6px',
                cursor: loading || formData.provider === 'local' ? 'not-allowed' : 'pointer',
                opacity: loading || formData.provider === 'local' ? 0.6 : 1,
              }}
            >
              {loading ? '⏳ Test ediliyor...' : '🔍 Bağlantıyı Test Et'}
            </button>

            <button
              onClick={handleSave}
              disabled={loading}
              style={{
                flex: 1,
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                fontWeight: '600',
                color: 'white',
                backgroundColor: '#28a745',
                border: 'none',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? '⏳ Kaydediliyor...' : '💾 Kaydet ve Aktif Et'}
            </button>
          </div>
        </div>
      </div>

      {/* Bilgi Kutusu */}
      <div style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffeaa7' }}>
        <h4 style={{ margin: '0 0 1rem 0', color: '#856404' }}>📚 Nasıl Kullanılır?</h4>
        <ol style={{ margin: 0, paddingLeft: '1.5rem', color: '#856404' }}>
          <li>Yukarıdan kullanmak istediğiniz otel yönetim sistemini seçin</li>
          <li>Gerekli bilgileri (API URL, Email, Şifre) girin</li>
          <li>"Bağlantıyı Test Et" butonuna tıklayarak bağlantıyı kontrol edin</li>
          <li>Test başarılıysa "Kaydet ve Aktif Et" butonuna basın</li>
          <li>Artık oda rezervasyonları seçtiğiniz sisteme otomatik gönderilecek!</li>
        </ol>
      </div>
    </div>
  );
};

export default HotelIntegrationTab;
